import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, todayISO, fmtDate } from "../lib/api";
import { Plus, Trash2, Edit2, Fuel, Users, Cookie, Home, Zap, Receipt, Wallet, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "fuel", label: "Fuel", icon: Fuel, color: "bg-amber-500" },
  { key: "salary", label: "Salary", icon: Users, color: "bg-sky-600" },
  { key: "snacks", label: "Snacks", icon: Cookie, color: "bg-rose-500" },
  { key: "rent", label: "Rent", icon: Home, color: "bg-zinc-900" },
  { key: "eb_bill", label: "EB Bill", icon: Zap, color: "bg-yellow-500" },
  { key: "other", label: "Other", icon: Receipt, color: "bg-zinc-500" },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

export default function Expenses() {
  const [list, setList] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [category, setCategory] = useState("fuel");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const res = await api.get("/expenses");
    setList(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditingId(null);
    setDate(todayISO());
    setCategory("fuel");
    setAmount("");
    setNotes("");
  };

  const save = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const payload = { date, category, amount: amt, notes };
    if (editingId) {
      await api.put(`/expenses/${editingId}`, payload);
      toast.success("Expense updated");
    } else {
      await api.post("/expenses", payload);
      toast.success(`${CAT_MAP[category].label} expense recorded`);
    }
    reset();
    load();
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setDate(e.date);
    setCategory(e.category);
    setAmount(String(e.amount));
    setNotes(e.notes || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    if (editingId === id) reset();
    load();
  };

  // Filter to current month for summary
  const summary = useMemo(() => {
    const thisMonth = todayISO().slice(0, 7); // YYYY-MM
    const monthList = list.filter((e) => e.date.startsWith(thisMonth));
    const byCat = {};
    CATEGORIES.forEach((c) => (byCat[c.key] = 0));
    let total = 0;
    monthList.forEach((e) => {
      byCat[e.category] = (byCat[e.category] || 0) + e.amount;
      total += e.amount;
    });
    return { total, byCat, count: monthList.length, month: thisMonth };
  }, [list]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="expenses-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Running Costs
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Expenses
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-xl">
          Track day-to-day overheads. These are deducted from sales when computing
          profit on Dashboard, Reports &amp; Insights.
        </p>
      </div>

      {/* Monthly summary */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
          <Wallet className="h-3 w-3" />
          This Month · {summary.month}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <div className="border border-zinc-900 bg-zinc-900 text-white p-4" data-testid="month-total">
            <div className="text-[10px] uppercase tracking-wider text-zinc-300">
              Total
            </div>
            <div className="font-display text-2xl font-bold font-mono-num mt-1">
              {fmtINR(summary.total)}
            </div>
            <div className="text-[10px] text-zinc-400 mt-1">
              {summary.count} entries
            </div>
          </div>
          {CATEGORIES.map((c) => (
            <div key={c.key} className="border border-zinc-200 bg-white p-4" data-testid={`cat-${c.key}`}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                <c.icon className="h-3 w-3" /> {c.label}
              </div>
              <div className="font-display text-xl font-bold font-mono-num mt-1">
                {fmtINR(summary.byCat[c.key] || 0)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Entry form */}
        <div className="border border-zinc-200 bg-white p-5 h-fit" data-testid="expense-form">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
            Add Expense
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="expense-date"
                className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Category
              </label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    data-testid={`cat-btn-${c.key}`}
                    className={`flex flex-col items-center gap-1 border px-2 py-2 text-[10px] uppercase tracking-wider ${
                      category === c.key
                        ? "border-sky-600 bg-sky-50 text-sky-700"
                        : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                    }`}
                  >
                    <c.icon className="h-4 w-4" />
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Amount ₹
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="expense-amount"
                className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num text-lg focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Notes (optional)
              </label>
              <input
                placeholder="e.g. Diesel for delivery"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="expense-notes"
                className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
            </div>
            <button
              onClick={save}
              data-testid="expense-save"
              className="w-full bg-sky-600 text-white py-2.5 font-medium uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-1"
            >
              <Plus className="h-4 w-4" />
              {editingId ? "Update Expense" : "Save Expense"}
            </button>
          </div>
        </div>

        {/* History */}
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Recent History
          </div>
          <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="expense-list">
            {list.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">
                No expenses recorded yet
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-left">Notes</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-right w-12">·</th>
                  </tr>
                </thead>
                <tbody>
                  {list.slice(0, 50).map((e) => {
                    const c = CAT_MAP[e.category] || CAT_MAP.other;
                    return (
                      <tr
                        key={e.id}
                        className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
                        data-testid={`expense-row-${e.id}`}
                      >
                        <td className="px-4 py-2.5 text-xs">{fmtDate(e.date)}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider">
                            <c.icon className="h-3 w-3" />
                            {c.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-zinc-600">
                          {e.notes || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-sm font-mono-num text-right font-semibold">
                          {fmtINR(e.amount)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => startEdit(e)}
                              className="text-zinc-400 hover:text-sky-600"
                              data-testid={`expense-edit-${e.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => del(e.id)}
                              className="text-zinc-300 hover:text-rose-600"
                              data-testid={`expense-delete-${e.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
